import React from 'react';
import { Table, TableBody, TableCell, TableRow } from '@mui/material';
import DataTableHeader from './DataTableHeader';
import { ColumnConfig } from '../../hooks/useTablePreferences';

interface DataTableBodyProps<T> {
  data: T[];
  visibleColumns: string[];
  columns: ColumnConfig[];
  getColumnWidth: (key: string) => number;
  getColumnValue: (item: T, columnKey: string) => React.ReactNode;
  onRowClick?: (e: React.MouseEvent, item: T) => void;
  getRowKey: (item: T) => string | number;
  onColumnResize: (key: string, newWidth: number) => void;
  onSort: (columnKey: string) => void;
  getSortIndicator: (columnKey: string) => string | null;
  totalWidth: number;
}

/**
 * Generic table body component that renders rows with visible columns.
 * Includes the Table element, header, and body — meant to be placed inside DataTableContainer.
 */
function DataTableBodyComponent<T>({
  data,
  visibleColumns,
  columns,
  getColumnWidth,
  getColumnValue,
  onRowClick,
  getRowKey,
  onColumnResize,
  onSort,
  getSortIndicator,
  totalWidth,
}: DataTableBodyProps<T>) {
  return (
    <Table
      style={{
        width: `${totalWidth}px`,
        minWidth: `${totalWidth}px`,
        borderCollapse: 'collapse',
        backgroundColor: '#fff',
        tableLayout: 'fixed',
      }}
    >
      <DataTableHeader
        columns={columns}
        visibleColumns={visibleColumns}
        getColumnWidth={getColumnWidth}
        onColumnResize={onColumnResize}
        onSort={onSort}
        getSortIndicator={getSortIndicator}
      />
      <TableBody>
        {data.map((item) => (
          <TableRow
            key={getRowKey(item)}
            onClick={onRowClick ? (e) => onRowClick(e, item) : undefined}
            sx={{
              cursor: onRowClick ? 'pointer' : 'default',
              '&:hover': {
                backgroundColor: 'grey.50',
              },
              transition: 'background-color 0.2s',
            }}
          >
            {visibleColumns.map((columnKey) => {
              const width = getColumnWidth(columnKey);
              return (
                <TableCell
                  key={columnKey}
                  sx={{
                    width: `${width}px`,
                    minWidth: `${width}px`,
                    maxWidth: `${width}px`,
                    padding: '12px 8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {getColumnValue(item, columnKey)}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default DataTableBodyComponent;
